import { Alert } from "antd";
import { FunctionComponent, useContext, useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import {
  ROUTE_DASHBOARD_GEARS,
  ROUTE_DASHBOARD_GEAR_CREATE,
  ROUTE_DASHBOARD_GEAR_DETAIL,
  ROUTE_DASHBOARD_GEAR_EDIT,
  ROUTE_DASHBOARD_MY_GEARS,
  ROUTE_DASHBOARD_ORGANIZATION, ROUTE_DASHBOARD_ORGANIZATION_EDIT,
  ROUTE_DASHBOARD_PROFILE,
  ROUTE_DASHBOARD_ROLES,
  ROUTE_DASHBOARD_ROLE_CREATE,
  ROUTE_DASHBOARD_ROLE_DETAIL,
  ROUTE_DASHBOARD_ROLE_EDIT,
  ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATIONS,
  ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATION_DETAIL,
  ROUTE_DASHBOARD_SUPER_ADMIN_USERS,
  ROUTE_DASHBOARD_SUPER_ADMIN_USER_DETAIL,
  ROUTE_DASHBOARD_TAGS,
  ROUTE_DASHBOARD_TAG_CREATE,
  ROUTE_DASHBOARD_TAG_DETAIL,
  ROUTE_DASHBOARD_TAG_EDIT,
  ROUTE_DASHBOARD_USERS,
  ROUTE_DASHBOARD_USER_CREATE,
  ROUTE_DASHBOARD_USER_DETAIL,
  ROUTE_DASHBOARD_USER_EDIT,
  ROUTE_DASHBOARD_WELCOME
} from "../../../shared/app-constants";
import getDashboardRoute from "../../../shared/get-dashboard-route";
import getPublicRoute from "../../../shared/get-public-route";
import GuardedRoute from "../../../shared/guarded-route";
import NotFoundPage from "../../../shared/not-found";
import Permissions from "../../../shared/permissions";
import UserStatus from "../../../shared/user-status";
import { AppContext } from "../../contexts/app-context";
import DashboardLayout from "../../layouts/dashboard-layout";
import GearCreatePage from "../../pages/gear/create";
import GearDetailPage from "../../pages/gear/detail";
import GearEditPage from "../../pages/gear/edit";
import GearListPage from "../../pages/gear/list";
import OrganizationDetailPage from "../../pages/organization/detail";
import OrganizationDetailSuperAdminPage from "../../pages/organization/detail-super-admin";
import OrganizationUpdatePage from "../../pages/organization/edit";
import OrganizationListPage from "../../pages/organization/list";
import ProfilePage from "../../pages/profile-page";
import RoleCreatePage from "../../pages/role/create";
import RoleDetailPage from "../../pages/role/detail";
import RoleUpdatePage from "../../pages/role/edit";
import RoleListPage from "../../pages/role/list";
import TagCreatePage from "../../pages/tag/create";
import TagDetailPage from "../../pages/tag/detail";
import TagUpdatePage from "../../pages/tag/edit";
import TagListPage from "../../pages/tag/list";
import UserCreatePage from "../../pages/user/create";
import UserDetailPage from "../../pages/user/detail";
import UserDetailSuperAdminPage from "../../pages/user/detail-superadmin";
import UserUpdatePage from "../../pages/user/edit";
import UserListPage from "../../pages/user/list";
import UserListSuerAdminPage from "../../pages/user/list-superadmin";
import WelcomePage from "../../pages/welcome";

interface DashboardModuleProps { }

const DashboardModule: FunctionComponent<DashboardModuleProps> = () => {
  const { user } = useContext(AppContext);
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) {
      navigate({ pathname: `/${getPublicRoute({})}` });
    }
    const onMentionClick = (e: Event) => {
      let target = e.target as HTMLElement;
      if (
        target &&
        !(target.tagName === "SPAN" && target.classList.contains("mention"))
      ) {
        target = target.closest("span.mention") as HTMLElement;
      }
      // if the target is span and has mention class in it
      if (target?.tagName === "SPAN" && target?.classList.contains("mention")) {
        const id = target.getAttribute("data-id");
        if (id) {
          navigate(getDashboardRoute({ id }, ROUTE_DASHBOARD_GEAR_DETAIL));
        }
      }
    };
    document.addEventListener("click", onMentionClick);
    return () => {
      document.removeEventListener("click", onMentionClick);
    };
  }, [user, navigate]);
  return (
    <DashboardLayout>
      {!(
        user?.is_super_admin ||
        user?.user_status === UserStatus.Active ||
        user?.id === user?.organization?.owner_id
      ) && (
          <Alert
            showIcon
            type="warning"
            message="Account is not active!"
            description="Your account is not active yet. You may not access all features until your account is activated. Please! ask your organization admin to activate your account."
          />
        )}
      <Routes>
        <Route
          path={""}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <GearListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_GEARS}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <GearListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_MY_GEARS}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <GearListPage self={true} />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_GEAR_CREATE}
          element={
            <GuardedRoute permissions={[Permissions.ADD_GEAR]}>
              <GearCreatePage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_GEAR_DETAIL}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <GearDetailPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_GEAR_EDIT}
          element={
            <GuardedRoute permissions={[Permissions.EDIT_GEAR]}>
              <GearEditPage />
            </GuardedRoute>
          }
        />
        <Route path={ROUTE_DASHBOARD_PROFILE} element={<ProfilePage />} />
        <Route
          path={ROUTE_DASHBOARD_ORGANIZATION}
          element={
            <GuardedRoute permissions={[Permissions.ORGANIZATION_MANAGEMENT]}>
              <OrganizationDetailPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_ORGANIZATION_EDIT}
          element={
            <GuardedRoute permissions={[Permissions.ORGANIZATION_MANAGEMENT]}>
              <OrganizationUpdatePage />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTE_DASHBOARD_ROLES}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.VIEW_ROLE]}
            >
              <RoleListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_ROLE_CREATE}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.ADD_ROLE]}
            >
              <RoleCreatePage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_ROLE_DETAIL}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.VIEW_ROLE]}
            >
              <RoleDetailPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_ROLE_EDIT}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.EDIT_ROLE]}
            >
              <RoleUpdatePage />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTE_DASHBOARD_USERS}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.VIEW_USER]}
            >
              <UserListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_USER_CREATE}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.ADD_USER]}
            >
              <UserCreatePage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_USER_DETAIL}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.VIEW_USER]}
            >
              <UserDetailPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_USER_EDIT}
          element={
            <GuardedRoute
              preventSuperAdminAccess
              permissions={[Permissions.EDIT_USER]}
            >
              <UserUpdatePage />
            </GuardedRoute>
          }
        />

        <Route
          path={ROUTE_DASHBOARD_TAGS}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <TagListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_TAG_CREATE}
          element={
            <GuardedRoute permissions={[Permissions.ADD_GEAR]}>
              <TagCreatePage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_TAG_DETAIL}
          element={
            <GuardedRoute permissions={[Permissions.VIEW_GEAR]}>
              <TagDetailPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_TAG_EDIT}
          element={
            <GuardedRoute permissions={[Permissions.EDIT_GEAR]}>
              <TagUpdatePage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATIONS}
          element={
            <GuardedRoute onlySuperAdminAccess>
              <OrganizationListPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_SUPER_ADMIN_ORGANIZATION_DETAIL}
          element={
            <GuardedRoute onlySuperAdminAccess>
              <OrganizationDetailSuperAdminPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_SUPER_ADMIN_USERS}
          element={
            <GuardedRoute onlySuperAdminAccess>
              <UserListSuerAdminPage />
            </GuardedRoute>
          }
        />
        <Route
          path={ROUTE_DASHBOARD_SUPER_ADMIN_USER_DETAIL}
          element={
            <GuardedRoute onlySuperAdminAccess>
              <UserDetailSuperAdminPage />
            </GuardedRoute>
          }
        />

        <Route path={ROUTE_DASHBOARD_WELCOME} element={<WelcomePage />} />
        <Route
          path="*"
          element={<NotFoundPage homePage={getDashboardRoute({})} />}
        />
      </Routes>
    </DashboardLayout>
  );
};

export default DashboardModule;
